// «Κατηγορίες εξόδων» (συρτάρι της σελίδας «Κινήσεις ταμείου»): προσθήκη, μετονομασία, σειρά (↑/↓) και
// ενεργή/ανενεργή. ΠΟΤΕ διαγραφή — η ανενεργή κρύβεται από το ταμείο, οι παλιές κινήσεις την κρατούν. Του
// ΟΡΓΑΝΙΣΜΟΥ (ίδιες σε όλα του τα καταστήματα) ή, σε ανεξάρτητο κατάστημα, δικές του. Κάθε αλλαγή
// αποθηκεύεται αμέσως· η απάντηση του server είναι η νέα λίστα.

import React, { useId, useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, Pencil, Plus } from 'lucide-react';
import { DetailPanel, GHOST_BTN, ICON_BTN, INPUT_CLS, Notice, PRIMARY_BTN, StatusChip } from '../../shared/listPage';
import { SettingsSwitch } from '../../settings/storeSettingsForm';
import { cashMovementsApi, CashMovementApiError, type AdminCategories } from './api';

const NAME_MAX = 60;

interface Props {
  storeId: number;
  open: boolean;
  data: AdminCategories | null;
  onChange: (next: AdminCategories) => void;
  onReload: () => Promise<void> | void;
  onClose: () => void;
}

type Busy = number | 'add' | 'order' | null;

export default function ExpenseCategoriesPanel({ storeId, open, data, onChange, onReload, onClose }: Props) {
  const uid = useId();
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<{ message: string; reactivateId?: number } | null>(null);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<{ id: number; name: string } | null>(null);
  const categories = data?.categories || [];
  const activeCount = categories.filter(c => c.active).length;

  const run = async (key: Busy, fn: () => Promise<AdminCategories>): Promise<boolean> => {
    if (busy !== null) return false;
    setBusy(key);
    setError(null);
    try {
      onChange(await fn());
      return true;
    } catch (e) {
      if (e instanceof CashMovementApiError) {
        if (e.code === 'categories_changed') await onReload();
        const reactivateId = e.code === 'category_exists' && e.body.active === false && typeof e.body.categoryId === 'number'
          ? e.body.categoryId : undefined;
        setError({ message: e.message, reactivateId });
      } else {
        setError({ message: 'Η αλλαγή δεν αποθηκεύτηκε — έλεγξε τη σύνδεση και ξαναδοκίμασε.' });
      }
      return false;
    } finally {
      setBusy(null);
    }
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    if (await run('add', () => cashMovementsApi.addCategory(storeId, name))) setNewName('');
  };

  const rename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const name = editing.name.trim();
    const current = categories.find(c => c.id === editing.id);
    if (!name || !current || name === current.name) { setEditing(null); return; }
    if (await run(editing.id, () => cashMovementsApi.updateCategory(storeId, editing.id, { name }))) setEditing(null);
  };

  const move = (index: number, delta: -1 | 1) => {
    const ids = categories.map(c => c.id);
    const to = index + delta;
    if (to < 0 || to >= ids.length) return;
    [ids[index], ids[to]] = [ids[to], ids[index]];
    run('order', () => cashMovementsApi.reorderCategories(storeId, ids));
  };

  const close = () => {
    if (busy !== null) return;
    setEditing(null);
    setError(null);
    onClose();
  };

  return (
    <DetailPanel
      open={open}
      onClose={close}
      title="Κατηγορίες εξόδων"
      subtitle={data?.scope === 'store' ? 'Του καταστήματος' : 'Κοινές για όλα τα καταστήματα του οργανισμού'}
      footer={<button type="button" className={GHOST_BTN} onClick={close} disabled={busy !== null}>Κλείσιμο</button>}
      testId="expense-categories-panel"
    >
      <div className="grid gap-4">
        <p className="text-sm text-t3">
          Δεν σβήνονται: η ανενεργή κρύβεται από το ταμείο και οι παλιές κινήσεις την κρατούν. Η μετονομασία φαίνεται
          και στις παλιές κινήσεις. Η σειρά εδώ είναι η σειρά στο ταμείο.
        </p>

        {error && (
          <Notice tone="danger" testId="expense-categories-error">
            <span role="alert">{error.message}</span>
            {error.reactivateId !== undefined && (
              <button type="button" className="ml-2 underline font-medium" data-testid="expense-category-reactivate"
                onClick={() => {
                  const id = error.reactivateId as number;
                  run(id, () => cashMovementsApi.updateCategory(storeId, id, { active: true })).then(ok => { if (ok) setNewName(''); });
                }}>
                Ενεργοποίηση
              </button>
            )}
          </Notice>
        )}

        <ol className="grid" aria-label="Κατηγορίες εξόδων" data-testid="expense-categories-list">
          {categories.map((c, i) => {
            const labelId = `${uid}-cat-${c.id}`;
            const lastActive = c.active && activeCount === 1;
            return (
              <li key={c.id} data-testid={`expense-category-${c.id}`}
                className="flex items-center gap-2 border-b border-line py-2 last:border-b-0">
                <div className="flex shrink-0 flex-col">
                  <button type="button" className={`${ICON_BTN} !h-7`} aria-label={`Πιο πάνω: ${c.name}`}
                    disabled={i === 0 || busy !== null} onClick={() => move(i, -1)}>
                    <ChevronUp className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button type="button" className={`${ICON_BTN} !h-7`} aria-label={`Πιο κάτω: ${c.name}`}
                    disabled={i === categories.length - 1 || busy !== null} onClick={() => move(i, 1)}>
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>

                {editing?.id === c.id ? (
                  <form className="flex min-w-0 flex-1 items-center gap-2" onSubmit={rename}>
                    {/* Esc ακυρώνει μόνο τη μετονομασία (το DetailPanel δεν κλείνει σε preventDefault). */}
                    <input className={INPUT_CLS} value={editing.name} maxLength={NAME_MAX} autoFocus
                      aria-label={`Νέο όνομα για «${c.name}»`} data-testid="expense-category-rename-input"
                      onChange={e => setEditing({ id: c.id, name: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); setEditing(null); } }} />
                    <button type="submit" className={PRIMARY_BTN} disabled={busy !== null || !editing.name.trim()}>Αποθήκευση</button>
                    <button type="button" className={GHOST_BTN} onClick={() => setEditing(null)} disabled={busy !== null}>Άκυρο</button>
                  </form>
                ) : (
                  <>
                    <span id={labelId} className={`min-w-0 flex-1 truncate ${c.active ? 'text-t1' : 'text-t3'}`}>
                      {c.name}
                      {!c.active && <> <StatusChip tone="muted">Ανενεργή</StatusChip></>}
                    </span>
                    {busy === c.id && <Loader2 className="h-4 w-4 animate-spin text-t3" aria-hidden="true" />}
                    <button type="button" className={ICON_BTN} aria-label={`Μετονομασία: ${c.name}`} disabled={busy !== null}
                      onClick={() => { setError(null); setEditing({ id: c.id, name: c.name }); }}>
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </>
                )}

                <span id={`${labelId}-active`} className="sr-only">{`Ενεργή: ${c.name}`}</span>
                <span title={lastActive ? 'Χρειάζεται τουλάχιστον μία ενεργή κατηγορία' : undefined}>
                  <SettingsSwitch checked={c.active} labelId={`${labelId}-active`} disabled={busy !== null || lastActive}
                    onChange={next => run(c.id, () => cashMovementsApi.updateCategory(storeId, c.id, { active: next }))} />
                </span>
              </li>
            );
          })}
        </ol>

        <form className="flex items-center gap-2" onSubmit={add}>
          <input className={INPUT_CLS} value={newName} maxLength={NAME_MAX} placeholder="Νέα κατηγορία (π.χ. Ενοίκιο)"
            aria-label="Όνομα νέας κατηγορίας" data-testid="expense-category-new" disabled={busy !== null}
            onChange={e => setNewName(e.target.value)} />
          <button type="submit" className={PRIMARY_BTN} disabled={busy !== null || !newName.trim()} data-testid="expense-category-add">
            {busy === 'add' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Plus className="h-4 w-4" aria-hidden="true" />}
            Προσθήκη
          </button>
        </form>
      </div>
    </DetailPanel>
  );
}
